import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMyProfile } from '@/features/profile';
import { Button } from '@/shared/ui/Button';
import { TextArea } from '@/shared/ui/TextArea';
import { TextField } from '@/shared/ui/TextField';
import { useUiStore } from '@/stores/ui.store';

import { useCreateTicket } from '../hooks/use-support';
import { deriveRoleAtCreation } from '../lib/ticket-role';
import { SUPPORT_TICKET_PRIORITIES, type SupportTicketPriority } from '../model/support-ticket';

export function NewTicketRoute(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateTicket();
  const profile = useMyProfile().data;
  const activeMode = useUiStore((s) => s.activeMode);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<SupportTicketPriority>('normal');
  const [error, setError] = useState<string | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    const s = subject.trim();
    const b = body.trim();
    if (s.length < 3) return setError(t('support.subjectInvalid'));
    if (b.length < 3) return setError(t('support.bodyInvalid'));
    if (!profile) return;
    setError(null);
    create.mutate(
      {
        subject: s,
        body: b,
        roleAtCreation: deriveRoleAtCreation(profile, activeMode),
        priority,
        category: 'user',
        relatedApplicationId: null,
        relatedMasjidId: null,
      },
      {
        onSuccess: (id) => navigate(`/support/tickets/${id}`),
        onError: (e) => setError(e instanceof Error ? e.message : String(e)),
      },
    );
  }

  return (
    <main className="page stack">
      <h1>{t('support.newTicket')}</h1>
      <form className="stack" onSubmit={submit} noValidate>
        <TextField
          label={t('support.subject')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <label className="field">
          <span className="field__label">{t('support.priority')}</span>
          <select
            className="field__input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as SupportTicketPriority)}
          >
            {SUPPORT_TICKET_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`support.priority_${p}`)}
              </option>
            ))}
          </select>
        </label>
        <TextArea
          label={t('support.describeIssue')}
          placeholder={t('support.describeIssueHint')}
          value={body}
          maxLength={4000}
          onChange={(e) => setBody(e.target.value)}
        />
        {error ? (
          <p className="text-danger" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" loading={create.isPending} disabled={!profile}>
          {t('support.submit')}
        </Button>
      </form>
    </main>
  );
}
